/** The words of an agreement card (spec-63 E4, design §4.6). Never colour alone (IA-20). */
import { todayIso } from '../dates'
import { overdueText, shortDay } from '../record/format'
import type { Agreement, AgreementBadge, IntervalUnit, Occurrence, Schedule } from './types'

const BADGE_TEXT: Record<AgreementBadge, string> = {
  achterstallig: 'Achterstallig',
  binnenkort: 'Binnenkort',
  op_schema: 'Op schema',
  gepauzeerd: 'Gepauzeerd',
  beeindigd: 'Beëindigd',
  verlopen: 'Verlopen',
  loopt_af: 'Loopt af',
}

/** The tone of a badge: danger, warning, success or neutral (`AgreementStatusBadge`). */
export const BADGE_TONE: Record<AgreementBadge, 'danger' | 'warning' | 'success' | 'neutral'> = {
  achterstallig: 'danger',
  binnenkort: 'warning',
  op_schema: 'success',
  gepauzeerd: 'neutral',
  beeindigd: 'neutral',
  verlopen: 'warning',
  loopt_af: 'warning',
}

export function badgeText(agreement: Agreement): string {
  if (!agreement.badge) return ''
  if (agreement.badge === 'verlopen' && agreement.ends_on) return `Verlopen op ${shortDay(agreement.ends_on)}`
  if (agreement.badge === 'loopt_af' && agreement.ends_on) return `Loopt af op ${shortDay(agreement.ends_on)}`
  return BADGE_TEXT[agreement.badge]
}

/** "Elk kwartaal · deadline 7 okt · 8 dagen te laat", "Contactritme elke maand · uiterlijk 13 nov". */
export function followUpLine(agreement: Agreement, today: string = todayIso()): string {
  const due = agreement.open_deadline?.due_on ?? agreement.next_due_on
  if (agreement.schedule === 'geen' || !due) {
    return agreement.ends_on ? `${agreement.rhythm_text} · loopt af ${shortDay(agreement.ends_on, today)}` : agreement.rhythm_text
  }
  const word = agreement.schedule === 'contactritme' ? 'uiterlijk' : 'deadline'
  const parts = [agreement.rhythm_text, `${word} ${shortDay(due, today)}`]
  const postponed = agreement.open_deadline?.task_due_date
  if (postponed && postponed !== due) parts.push(`uitgesteld tot ${shortDay(postponed, today)}`)
  else if (agreement.badge === 'achterstallig') {
    const late = overdueText(due, today)
    if (late) parts.push(late)
  }
  return parts.join(' · ')
}

export function historyText(occurrence: Occurrence): string {
  const day = shortDay(occurrence.due_on)
  if (occurrence.status === 'afgehandeld') {
    return occurrence.done_on && occurrence.done_on !== occurrence.due_on ? `✓ ${day} (op ${shortDay(occurrence.done_on)})` : `✓ ${day}`
  }
  if (occurrence.status === 'overgeslagen') return `✗ ${day} overgeslagen`
  if (occurrence.status === 'vervallen') return `⏸ ${day} vervallen`
  return day
}

export function partyText(agreement: Pick<Agreement, 'organisation' | 'person'>): string {
  return [agreement.organisation?.name, agreement.person?.name].filter(Boolean).join(' · ')
}

/** ED-26: week 2, maand 7, jaar 30 days before; one-off and contact rhythm 7. The server applies the same. */
export function defaultLeadDays(schedule: Schedule, unit: IntervalUnit | null): number {
  if (schedule === 'terugkerend' && unit) return { week: 2, maand: 7, jaar: 30 }[unit]
  return 7
}

/** The state of the contact rhythms of a relation, for the key fact "Contactritme" (design §4.4). */
export function rhythmState(agreements: Agreement[], today: string = todayIso()): 'geen' | 'op_schema' | 'binnenkort' | 'gemist' {
  const rhythms = agreements.filter((agreement) => agreement.schedule === 'contactritme' && agreement.status === 'actief')
  if (rhythms.length === 0) return 'geen'
  const due = (agreement: Agreement) => agreement.open_deadline?.due_on ?? agreement.next_due_on
  if (rhythms.some((agreement) => (due(agreement) ?? '9999') < today)) return 'gemist'
  if (rhythms.some((agreement) => agreement.open_deadline || (agreement.task_from !== null && agreement.task_from <= today))) return 'binnenkort'
  return 'op_schema'
}

/** Where a row leads: the relation's Afspraken tab, the organisation first. */
export function agreementHref(agreement: Pick<Agreement, 'organisation' | 'person'>): string {
  if (agreement.organisation) return `/relaties/organisaties/${agreement.organisation.id}?tab=afspraken`
  return `/relaties/contacten/${agreement.person?.id}?tab=afspraken`
}
